import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, ModuleWithProviders, NgModule } from '@angular/core';

import { NgxMaterialIntlTelInputComponent } from 'ngx-material-intl-tel-input';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { PickerModule } from '@ctrl/ngx-emoji-mart';
import { MatNativeDateTimeModule, MatTimepickerModule } from '@dhutaryan/ngx-mat-timepicker';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';

import { IMessagingEnvironment } from '../../core/interfaces/IMessagingEnvironment';
import { MESSAGING_CONFIG } from '../../core/messaging-config';
import { AwsService } from '../../core/services/aws.service';
import { DarkModeService } from '../../core/services/dark-mode.service';
import { MessagingEnvironmentService } from '../../core/services/messaging-environment.service';
import { SnackBarService } from '../../core/services/snackbar.service';
import { WorkspaceDrawerComponent } from '../chat/components/workspace-drawer/workspace-drawer.component';
import { ImagesService } from '../shared/services/images.service';
import { NgxMessagingSharedModule } from '../shared/shared.module';
import { AuthInterceptor } from '../user/services/auth.interceptor';
import { AuthenticationService } from '../user/services/authentication.service';
import { UserEffects } from '../user/store/user.effects';
import { AttachmentsComponent } from './components/attachments/attachments.component';
import { CallReciveModalComponent } from './components/call-recive/call-recive.component';
import { ChannelBlankComponent } from './components/channel-blank/channel-blank.component';
import { ChannelBodyComponent } from './components/channel-body/channel-body.component';
import { ChannelEditFormComponent } from './components/channel-edit-form/channel-edit-form.component';
import { ChannelHeaderComponent } from './components/channel-header/channel-header.component';
import { ChannelInfoComponent } from './components/channel-info/channel-info.component';
import { ChannelInputComponent } from './components/channel-input/channel-input.component';
import { ChannelSkeletonsComponent } from './components/channel-skeletons/channel-skeletons.component';
import { ChannelStatusComponent } from './components/channel-status/channel-status.component';
import { ChannelComponent } from './components/channel/channel.component';
import { ChannelsListFiltersComponent } from './components/channels-list-filters/channels-list-filters.component';
import { CheckFilterComponent } from './components/channels-list-filters/check-filter/check-filter.component';
import { SelectorFilterComponent } from './components/channels-list-filters/selector-filter/selector-filter.component';
import { ChannelsListItemComponent } from './components/channels-list-item/channels-list-item.component';
import { ChannelsListSearchComponent } from './components/channels-list-search/channels-list-search.component';
import { ChannelsListComponent } from './components/channels-list/channels-list.component';
import { ChannelsComponent } from './components/channels/channels.component';
import { EmojiModalComponent } from './components/emoji/emoji.component';
import { GenericSpinnerComponent } from './components/generic-sprinner/generic-spinner.component';
import { GiphyModalComponent } from './components/giphy/giphy.component';
import { JoinChannelComponent } from './components/join-channel/join-channel.component';
import { LanguageSelectorComponent } from './components/languaje-selector/language-selector.component';
import { LastMessagesBannerComponent } from './components/last-messages-banner/last-messages-banner.component';
import { LayoutComponent } from './components/layout/layout.component';
import { ListMembersChannelToCallModalComponent } from './components/list-members-channel-to-call/list-members-channel-to-call.component';
import { ManualStatusComponent } from './components/manual-status/manual-status.component';
import { MediaComponent } from './components/media-upload/media-upload.component';
import { MembersComponent } from './components/members/members.component';
import { MessageComponent } from './components/message/message.component';
import { NewBroadcastChannelComponent } from './components/new-chat-dialog/new-broadcast-channel/new-broadcast-channel.component';
import { NewDirectChatComponent } from './components/new-chat-dialog/new-direct-chat/new-direct-chat.component';
import { NewGroupChatComponent } from './components/new-chat-dialog/new-group-chat/new-group-chat.component';
import { NewPhoneChatComponent } from './components/new-chat-dialog/new-phone-chat/new-phone-chat.component';
import { OldMessagesBannerComponent } from './components/old-messages-banner/old-messages-banner.component';
import { OpenModalComponent } from './components/open-modal/open-modal.component';
import { OwnerChangeComponent } from './components/owner-change/owner-change.component';
import { PhoneEditFormComponent } from './components/phone-edit-form/phone-edit-form.component';
import { ProfileActionsComponent } from './components/profile-actions/profile-actions.component';
import { RepairOrderComponent } from './components/repair-order/repair-order.component';
import { SelectUsersModalComponent } from './components/select-users/select-users.component';
import { SmartRepliesComponent } from './components/smart-replies/smart-replies.component';
import { StandardResponsesComponent } from './components/standards-responses/standard-responses.component';
import { TagsComponent } from './components/tags/tags.component';
import { TextProcessorModalComponent } from './components/text-processor/text-processor.component';
import { TextSpinnerComponent } from './components/text-spinner/text-spinner.component';
import { UsersSearchComponent } from './components/users-search/users-search.component';
import { UsersSelectedComponent } from './components/users-selected/users-selected.component';
import { VideoConferenceComponent } from './components/video-conference/video-conference.component';
import { VideoComponent } from './components/video/video.component';
import { NgxMessagingAwayComponent } from './external-components/ngx-messaging-away/ngx-messaging-away.component';
import { MessagingChannelComponent } from './external-components/ngx-messaging-channel/ngx-messaging-channel.component';
import { MessagingChatComponent } from './external-components/ngx-messaging-chat/ngx-messaging-chat.component';
import { MessagingLayoutComponent } from './external-components/ngx-messaging-layout/ngx-messaging-layout.component';
import { ChatPipesModule } from './pipes/pipes.module';
import { AlternateTranslationService } from './services/alternate-translation.service';
import { AttachmentsService } from './services/attachments.service';
import { ChannelsService } from './services/channels.service';
import { ContactDirectoryService } from './services/contact-directory/contact-directory-api.service';
import { DatesService } from './services/dates.service';
import { DenoiserService } from './services/denoise.service';
import { EncryptService } from './services/encrypt.service';
import { GiphyService } from './services/giphy.service';
import { LogsService } from './services/logs.service';
import { MessagesService } from './services/messages.service';
import { MessagingApiService } from './services/messaging-api/messaging-api.service';
import { ChannelsApiService } from './services/messaging-api/channels-api.service';
import { IntegrationsApiService } from './services/messaging-api/integrations-api.service';
import { MessagesApiService } from './services/messaging-api/messages-api.service';
import { MessagingApiStateService } from './services/messaging-api/messaging-api-state.service';
import { TagsApiService } from './services/messaging-api/tags-api.service';
import { UsersApiService } from './services/messaging-api/users-api.service';
import { RxStompService } from './services/messaging-ws.service';
import { NotificationsApiService } from './services/notifications-api.service';
import { PhonesService } from './services/phones.service';
import { ResizeService } from './services/resize.service';
import { SmartRepliesService } from './services/smart-replies.service';
import { SoundsService } from './services/sounds.service';
import { StandardRepliesService } from './services/standard-replies.service';
import { TextProcessorService } from './services/text-processor.service';
import { TranslaterService } from './services/translater.service';
import { MessagingTranslationService } from './services/translations.service';
import { WebsocketService } from './services/websocket.service';
import { WorkspacesService } from './services/workspaces.service';
import { ChannelsEffects } from './store/channels.effects';
import { MessagesEffects } from './store/messages.effects';
import { NotificationsEffects } from './store/notifications.effects';
import * as chatReducer from './store/chat.reducer';

@NgModule({
  declarations: [
    MessagingChannelComponent,
    CallReciveModalComponent,
    ChannelsComponent,
    ChannelsListComponent,
    ChannelsListItemComponent,
    ChannelsListFiltersComponent,
    ChannelsListSearchComponent,
    ChannelBodyComponent,
    ChannelHeaderComponent,
    ChannelInfoComponent,
    ChannelInputComponent,
    EmojiModalComponent,
    GenericSpinnerComponent,
    GiphyModalComponent,
    JoinChannelComponent,
    ListMembersChannelToCallModalComponent,
    MediaComponent,
    MembersComponent,
    MessageComponent,
    NewBroadcastChannelComponent,
    NewDirectChatComponent,
    NewGroupChatComponent,
    NewPhoneChatComponent,
    OpenModalComponent,
    RepairOrderComponent,
    SelectUsersModalComponent,
    SmartRepliesComponent,
    TextProcessorModalComponent,
    VideoConferenceComponent,
    VideoComponent,
    MessagingLayoutComponent,
    ProfileActionsComponent,
    WorkspaceDrawerComponent,
    UsersSelectedComponent,
    UsersSearchComponent,
    TagsComponent,
    CheckFilterComponent,
    SelectorFilterComponent,
    ChannelBlankComponent,
    StandardResponsesComponent,
    ManualStatusComponent,
    AttachmentsComponent,
    TextSpinnerComponent,
    ChannelSkeletonsComponent,
    ChannelStatusComponent,
    OldMessagesBannerComponent,
    ChannelEditFormComponent,
    ChannelComponent,
    LayoutComponent,
    LastMessagesBannerComponent,
    MessagingChatComponent,
    NgxMessagingAwayComponent,
    PhoneEditFormComponent,
    OwnerChangeComponent,
    LanguageSelectorComponent,
  ],
  exports: [
    MessagingChannelComponent,
    CallReciveModalComponent,
    ChannelsComponent,
    ChannelsListFiltersComponent,
    ChannelsListSearchComponent,
    ChannelBodyComponent,
    ChannelHeaderComponent,
    ChannelInfoComponent,
    ChannelInputComponent,
    EmojiModalComponent,
    GenericSpinnerComponent,
    GiphyModalComponent,
    JoinChannelComponent,
    ListMembersChannelToCallModalComponent,
    MediaComponent,
    MembersComponent,
    MessageComponent,
    NewBroadcastChannelComponent,
    NewDirectChatComponent,
    NewGroupChatComponent,
    OpenModalComponent,
    RepairOrderComponent,
    SelectUsersModalComponent,
    SmartRepliesComponent,
    TextProcessorModalComponent,
    VideoConferenceComponent,
    NgxMessagingSharedModule,
    TranslateModule,
    ChatPipesModule,
    VideoComponent,
    MessagingLayoutComponent,
    ChannelsListComponent,
    ChannelsListItemComponent,
    ProfileActionsComponent,
    WorkspaceDrawerComponent,
    UsersSelectedComponent,
    UsersSearchComponent,
    TagsComponent,
    CheckFilterComponent,
    SelectorFilterComponent,
    ManualStatusComponent,
    ChannelBlankComponent,
    StandardResponsesComponent,
    NewPhoneChatComponent,
    AttachmentsComponent,
    TextSpinnerComponent,
    ChannelSkeletonsComponent,
    ChannelStatusComponent,
    OldMessagesBannerComponent,
    ChannelEditFormComponent,
    ChannelComponent,
    LayoutComponent,
    LastMessagesBannerComponent,
    MessagingChatComponent,
    NgxMessagingAwayComponent,
    PhoneEditFormComponent,
    OwnerChangeComponent,
    LanguageSelectorComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    NgxSkeletonLoaderModule,
    NgxMessagingSharedModule,
    ChatPipesModule,
    EffectsModule.forFeature([ChannelsEffects, MessagesEffects, NotificationsEffects, UserEffects]),
    StoreModule.forFeature('chat', chatReducer.reducer),
    TranslateModule,
    PickerModule,
    NgxMaterialIntlTelInputComponent,
    MatNativeDateTimeModule,
    MatTimepickerModule,
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    MessagingTranslationService,
    MessagingApiStateService,
    ChannelsApiService,
    MessagesApiService,
    UsersApiService,
    TagsApiService,
    IntegrationsApiService,
    MessagingApiService,
    WebsocketService,
    TranslaterService,
    TextProcessorService,
    LogsService,
    SnackBarService,
    DarkModeService,
    AwsService,
    AuthenticationService,
    ContactDirectoryService,
    SoundsService,
    DenoiserService,
    EncryptService,
    AlternateTranslationService,
    GiphyService,
    SmartRepliesService,
    WorkspacesService,
    MessagesService,
    StandardRepliesService,
    NotificationsApiService,
    ChannelsService,
    AttachmentsService,
    ImagesService,
    ResizeService,
    PhonesService,
    DatesService,
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
  ],
})
export class MessagingChatModule {
  public static messagingEnvironment: IMessagingEnvironment;

  static forRoot(options: {
    messagingEnvironment: IMessagingEnvironment;
    rxStompServiceFactory: () => RxStompService;
  }): ModuleWithProviders<MessagingChatModule> {
    this.messagingEnvironment = options.messagingEnvironment;
    return {
      ngModule: MessagingChatModule,
      providers: [
        MessagingEnvironmentService,
        { provide: MESSAGING_CONFIG, useValue: this.messagingEnvironment },
        { provide: RxStompService, useFactory: options.rxStompServiceFactory },
      ],
    };
  }
}
